import { useQueries, useQuery } from "@tanstack/react-query";

import { listPlanInstances, planStatus } from "../plan/plan-api.js";
import { useShell } from "../shell/shell-context.js";
import { listPlanChecks } from "./checklist-api.js";
import { currentRegistration, listRegistrations } from "./registration-api.js";
import {
  assetResource,
  cleaningResource,
  documentResource,
  methodResource,
  riskResource,
  supplierResource,
  waterResource,
} from "./resources.js";
import { listSetupItems, listStaff } from "./setup-api.js";
import {
  buildChecklist,
  checklistProgress,
  hasInvitedTeam,
} from "./setup-checklist-model.js";

// Today needs only the three ready-kitchen steps; the Setup page also counts
// what waits under "Before your verification".
export function useSetupChecklist({ full = false }: { full?: boolean } = {}) {
  const shell = useShell();
  const locationId = shell.activeLocation?.id;
  const enabled = Boolean(locationId) && shell.activeRole !== "staff";
  const later = [waterResource, riskResource, methodResource, documentResource];
  const extras = useQueries({
    queries: later.map((resource) => ({
      enabled: enabled && full,
      queryFn: () => listSetupItems(resource, locationId!),
      queryKey: ["setup", resource.key, locationId],
      staleTime: 30_000,
    })),
  });
  const [water, risks, methods, documents] = extras;
  const registrations = useQuery({
    enabled: enabled && full,
    queryFn: listRegistrations,
    queryKey: ["registrations"],
    staleTime: 30_000,
  });
  const [assets, suppliers, cleaning, plans, checks, staff] = useQueries({
    queries: [
      {
        enabled,
        queryFn: () => listSetupItems(assetResource, locationId!),
        queryKey: ["setup", assetResource.key, locationId],
        staleTime: 30_000,
      },
      {
        enabled,
        queryFn: () => listSetupItems(supplierResource, locationId!),
        queryKey: ["setup", supplierResource.key, locationId],
        staleTime: 30_000,
      },
      {
        enabled,
        queryFn: () => listSetupItems(cleaningResource, locationId!),
        queryKey: ["setup", cleaningResource.key, locationId],
        staleTime: 30_000,
      },
      {
        enabled,
        queryFn: () => listPlanInstances(locationId!),
        queryKey: ["plan-instances", locationId],
        staleTime: 30_000,
      },
      {
        enabled,
        queryFn: () => listPlanChecks(locationId!),
        queryKey: ["plan-checks", locationId],
        staleTime: 30_000,
      },
      {
        enabled,
        queryFn: () => listStaff(locationId!),
        queryKey: ["staff", locationId],
        staleTime: 30_000,
      },
    ],
  });
  const loadedStatus = planStatus(
    plans.data ?? [],
    (shell.activeLocation as { plan_status?: unknown } | null)?.plan_status,
  );
  // Until the plans load, an unknown status must not look like a missing plan
  // and briefly make "Review your plan" a required step.
  const status =
    plans.isPending && loadedStatus === "none" ? "needs_review" : loadedStatus;
  const steps = buildChecklist({
    assetCount: assets.data?.length ?? 0,
    checks: checks.data ?? [],
    cleaningCount: cleaning.data?.length ?? 0,
    documentCount: documents?.data?.length ?? 0,
    hasLocation: shell.locations.length > 0,
    hasTeam: hasInvitedTeam(staff.data ?? [], shell.ownStaffProfileIds),
    methodCount: methods?.data?.length ?? 0,
    registered: Boolean(
      locationId && currentRegistration(registrations.data ?? [], locationId),
    ),
    planStatus: status,
    riskCount: risks?.data?.length ?? 0,
    supplierCount: suppliers.data?.length ?? 0,
    waterCount: water?.data?.length ?? 0,
  });
  const loaded = [assets, suppliers, cleaning, plans, checks, staff].every(
    (query) => !enabled || !query.isPending,
  );
  return {
    ...checklistProgress(steps),
    loaded,
    steps,
  };
}
